import { Component, OnInit, signal, computed, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StockDeliveryResultRecord, OutboundDeliveryItem } from '../../stock-delivery.config';
import { StockDeliveryService } from '../../stock-delivery.service';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS } from '../../delivery-order/add/outbound-delivery-order-add.config';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { BASE_PATH, OutboundDeliveryOrderControllerService, ResultRecordControllerService } from '@app/api';
import {
  OutboundDeliveryOrderService,
  WarehouseControllerService,
} from '@app/api/stock-service';
import { environment } from '@environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import {
  formatDeliveryDate,
  getDeliveryStatusSeverity,
  getDeliveryStatusLabel,
  getOutboundTypeLabel,
} from '../../delivery.helpers';

@Component({
  selector: 'app-delivery-result-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    DataTableComponent,
    AppButtonComponent,
    ProgressSpinnerModule,
    TagModule,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    OutboundDeliveryOrderControllerService,
    OutboundDeliveryOrderService,
    ResultRecordControllerService,
    WarehouseControllerService,
    StockDeliveryService,
  ],
  templateUrl: './delivery-result-view.component.html',
  styleUrl: './delivery-result-view.component.css',
})
export class DeliveryResultViewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private deliveryService = inject(StockDeliveryService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  resultData = signal<StockDeliveryResultRecord | null>(null);
  productDetails = signal<any[]>([]);
  productTableColumns = signal<TableColumn[]>([]);

  // Outbound type signal
  outType = signal<string | undefined>(undefined);
  isTransferOrder = computed(() => this.outType() === 'transfer-out-stock');
  isSalesOrder = computed(() => this.outType() === 'order-sales-out-stock');

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBackClick(): void {
    this.router.navigate(['/stock/delivery/result']);
  }

  private loadData(): void {
    this.loading.set(true);

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
      });
      this.router.navigate(['/stock/delivery/result']);
      return;
    }

    // Step 1: Load result record detail
    this.deliveryService.getDeliveryResultDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: StockDeliveryResultRecord) => {
          if (data) {
            this.outType.set(data.outType);
            this.resultData.set(data);
            // Step 2: Load product details using odoId
            const odoId = data.odoId;
            if (odoId) {
              this.loadProductDetails(odoId);
            } else {
              this.loading.set(false);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
            });
            this.router.navigate(['/stock/delivery/result']);
            this.loading.set(false);
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
          });
          this.router.navigate(['/stock/delivery/result']);
          this.loading.set(false);
        }
      });
  }

  private loadProductDetails(odoId: string): void {
    this.deliveryService.getDeliveryOrderDetails(odoId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: OutboundDeliveryItem[]) => {
          this.initializeProductDetails(items);
          this.loading.set(false);
        },
        error: () => {
          this.productDetails.set([]);
          this.loading.set(false);
        }
      });
  }

  private initializeProductDetails(detailList: OutboundDeliveryItem[]): void {
    const baseColumns = OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS.filter((col: TableColumn) =>
      col.prop !== 'quantity' && col.prop !== 'alreadyOutQty'
    );

    const productTableColumns: TableColumn[] = [
      ...baseColumns.map((col: TableColumn) => col),
      {
        label: 'outbound_management.fields.planned_quantity',
        prop: 'plannedQty',
        width: 120,
        type: 'text',
        align: 'right',
      },
      {
        label: 'outbound_management.table.columns.quantity',
        prop: 'quantity',
        width: 120,
        type: 'text',
        align: 'right',
      },
    ];

    const formattedDetails = detailList.map((item, index) => {
      const shippedQty = item.alreadyOutQty !== undefined && item.alreadyOutQty !== null
        ? item.alreadyOutQty
        : (item.pickedQty !== undefined && item.pickedQty !== null ? item.pickedQty : null);

      return {
        ...item,
        serialNumber: index + 1,
        quantity: shippedQty !== null && shippedQty !== undefined
          ? this.formatNumber(shippedQty)
          : '-',
        plannedQty: typeof item.plannedQty === 'number' ? this.formatNumber(item.plannedQty) : (typeof item.plannedQty !== 'undefined' ? String(item.plannedQty) : '-'),
      };
    });

    this.productTableColumns.set(productTableColumns);
    this.productDetails.set(formattedDetails);
  }

  // Helper methods for template
  getFieldValue(fieldName: string): any {
    const data = this.resultData();
    if (!data) return null;
    const value = (data as any)[fieldName];
    return value !== undefined && value !== null ? value : null;
  }

  getOutboundTypeLabelValue(): string {
    const data = this.resultData();
    if (!data) return '-';
    return data.outTypeMeaning
      ? getOutboundTypeLabel(data.outTypeMeaning, this.translateService)
      : (data.outType ? getOutboundTypeLabel(data.outType, this.translateService) : '-');
  }

  getStatusLabel(status: string | number): string {
    const outTypeVal = this.outType();
    return getDeliveryStatusLabel(status, this.translateService, true, outTypeVal);
  }

  getStatusSeverity(status: string | number): string {
    return getDeliveryStatusSeverity(status);
  }

  getStatusTagClass(status: string | number): string {
    const severity = this.getStatusSeverity(status);
    return `status-tag p-tag-${severity}`;
  }

  formatDate = formatDeliveryDate;

  private formatNumber(value: number | undefined): string {
    if (value === undefined || value === null) {
      return '-';
    }
    return value.toLocaleString('vi-VN');
  }
}
